import { sql } from "drizzle-orm";
import { relations } from "drizzle-orm/_relations";
import {
  check,
  date,
  doublePrecision,
  index,
  integer,
  pgTable,
  serial,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

import { users } from "./auth";
import { levels } from "./levels";
import {
  collegeCommitmentLevelEnum,
  genderEnum,
  playerRoleEnum,
  rightLeftEnum,
  timestamps,
} from "./shared";

const { createInsertSchema, createSelectSchema, createUpdateSchema } =
  createSchemaFactory({ zodInstance: z });

export const playerProfiles = pgTable(
  "player_profiles",
  {
    id: serial().primaryKey(),
    userId: text().references(() => users.id, {
      onDelete: "set null",
    }),
    firstName: text().notNull(),
    preferredName: text(),
    lastName: text().notNull(),
    birthdate: date().notNull(),
    gender: genderEnum().notNull(),
    levelId: integer().references(() => levels.id, {
      onDelete: "set null",
    }),
    ratedPoints: doublePrecision().notNull().default(0),
    juniorsPoints: doublePrecision().notNull().default(0),
    rank: integer(),
    bio: text(),
    imageSource: text(),
    heightFeet: integer(),
    heightInches: integer(),
    dominantArm: rightLeftEnum(),
    preferredRole: playerRoleEnum(),
    preferredSide: rightLeftEnum(),
    club: text(),
    highSchoolGraduationYear: integer(),
    collegeTeam: text(),
    collegeTeamYearsParticipated: integer(),
    collegeCommitmentLevel: collegeCommitmentLevelEnum(),
    avpId: integer().unique(),
    externalRef: uuid().unique().notNull().defaultRandom(),
    // Set when the profile is anonymized for a deletion request. Non-null means
    // the profile is an inert "Deleted user" placeholder: it keeps its results
    // but must never appear in a search, picker, or ranking.
    deletedAt: timestamp(),
    ...timestamps,
  },
  (table) => [
    check(
      "height_feet_check",
      sql`${table.heightFeet} >= 0 AND ${table.heightFeet} <= 8`,
    ),
    check(
      "height_inches_check",
      sql`${table.heightInches} >= 0 AND ${table.heightInches} < 12`,
    ),
    index("player_profiles_user_id_idx").on(table.userId),
    index("player_profiles_rank_idx").on(table.rank),
    index("player_profiles_hs_grad_year_idx").on(
      table.highSchoolGraduationYear,
    ),
    index("player_profiles_level_id_idx").on(table.levelId),
    index("player_profiles_birthdate_idx").on(table.birthdate),
    index("player_profiles_last_name_idx").on(table.lastName),
    index("player_profiles_gender_rank_rated_points_idx").on(
      table.gender,
      table.rank,
      table.ratedPoints,
    ),
    index("player_profiles_active_idx")
      .on(table.id)
      .where(sql`${table.deletedAt} is null`),
    index("player_profiles_name_trgm_idx").using(
      "gin",
      sql`(
        CASE
          WHEN ${table.preferredName} IS NOT NULL AND ${table.preferredName} != ''
          THEN ${table.preferredName} || ' ' || ${table.lastName}
          ELSE ${table.firstName} || ' ' || ${table.lastName}
        END
      ) gin_trgm_ops`,
    ),
  ],
);

export const selectPlayerProfileSchema = createSelectSchema(playerProfiles);
export const createPlayerProfileSchema = createInsertSchema(
  playerProfiles,
).omit({
  id: true,
  externalRef: true,
});
export const updatePlayerProfileSchema = createUpdateSchema(
  playerProfiles,
).omit({
  id: true,
  externalRef: true,
});

export type PlayerProfile = z.infer<typeof selectPlayerProfileSchema>;
export type CreatePlayerProfile = z.infer<typeof createPlayerProfileSchema>;
export type UpdatePlayerProfile = z.infer<typeof updatePlayerProfileSchema>;

/**
 * Minimal profile columns for match display - only name, photo, and userId
 * (userId is needed for self-ref authorization checks).
 */
export const matchProfileColumns = {
  id: true,
  firstName: true,
  preferredName: true,
  lastName: true,
  imageSource: true,
  userId: true,
} as const;

/**
 * Public profile columns - excludes sensitive data like birthdate and userId.
 * Use this in relational queries when returning profiles to clients.
 */
export const publicProfileColumns = {
  id: true,
  firstName: true,
  preferredName: true,
  lastName: true,
  gender: true,
  levelId: true,
  ratedPoints: true,
  juniorsPoints: true,
  rank: true,
  bio: true,
  imageSource: true,
  heightFeet: true,
  heightInches: true,
  dominantArm: true,
  preferredRole: true,
  preferredSide: true,
  club: true,
  highSchoolGraduationYear: true,
  collegeTeam: true,
  collegeTeamYearsParticipated: true,
  collegeCommitmentLevel: true,
  avpId: true,
} as const;

export const playerProfileRelations = relations(playerProfiles, ({ one }) => ({
  user: one(users, {
    fields: [playerProfiles.userId],
    references: [users.id],
  }),
  level: one(levels, {
    fields: [playerProfiles.levelId],
    references: [levels.id],
  }),
}));
